import { Component, ElementRef, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router, Params  } from '@angular/router';
import { Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, map, retry, tap, timeout, take, filter, shareReplay } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';

import { TrackingSegmentService } from '@unico-people/shared/data-access-tracking-segment';
import { EmployeesService } from '../../services/employees/employees.service';
import { SkillService } from '../../services/skill/skill.service';
import { DataAccessOrganizationsService } from '@unico-people/shared/dashboard/data-access-organizations';
import { LaunchdarklyService } from '@acesso-io/launchdarkly-client-ts/lib/launchdarkly';

import { SegmentTrackList } from '@unico-people/shared/enums/utils-enums';
import { EmployeeData, EmployeeParams } from '../../interfaces/employee.model';

import * as fromStore from '@unico-people/shared/dashboard/data-access-store';
import { ItemCollaboratorComponent } from '../../components/item-collaborator/item-collaborator.component';
import { UiModalDefaultComponent } from '@unico-people/shared/dashboard/ui-modal-default';
import { ResponseSkillTrial, SkillTrialData } from '../../interfaces/requests.model';
import { SessionStorage } from '@unico-people/shared/utils/storage';

@Component({
  selector: 'up-employee-list',
  templateUrl: './employee-list.component.html',
  styleUrls: ['./employee-list.component.scss']
})
export class EmployeeListComponent implements OnInit {
  collaborators$: Observable<any>;
  departments$: Observable<any>;
  roles$: Observable<any>;
  units$: Observable<any>;
  error$ = new Subject<any>();

  @ViewChild('typeSelect') typeSelectListing: ElementRef;
  @ViewChildren(ItemCollaboratorComponent) itemsListing: QueryList<ItemCollaboratorComponent>;

  dialogStatusCollaborator: MatDialogRef<UiModalDefaultComponent>;

  listEmployees: Array<EmployeeData>;
  acc: string;
  account: string;
  searchQuery: string;
  order: string;
  emptyList: boolean;
  isUserEligibleForUnicoSkillTrial: boolean = false;
  changeStatus = true;

  unicoSkillTrial: SkillTrialData;

  errorOnLoading = false;
  isAscendingOrder = true;
  loading = true;
  hasFilter = false;
  someItemSelected = false;
  allItemsSelected = false;
  listItemsSelected = [];
  active = 'active';
  filterState = {
    departments: [],
    roles: [],
    units: []
  };
  limit = 10;
  page = 1;
  total = 0;

  constructor(
    public router: Router,
    public route: ActivatedRoute,
    private employeeService: EmployeesService,
    private skillService: SkillService,
    private dialog: MatDialog,
    private store: Store<fromStore.DashboardState>,
    private segment: TrackingSegmentService,
    private dataAccessOrganizations: DataAccessOrganizationsService,
    private launchDarklyService: LaunchdarklyService,
  ) {}

  ngOnInit(): void {
    this.segment.page(SegmentTrackList.page_view_listing);
    this.route.queryParams.subscribe((params: Params) => {
      this.account = params.a;
    });

    this.store.pipe(select(fromStore.getOrganizationState)).subscribe(res => {
      if (res?.currentAccount?.name && SessionStorage.getItem('currentAccount')) {
        this.getListingParams();
        this.checkEligibilityTrialUnicoSkill();
      }
    });

    this.router.navigate([], {
      queryParams: {
        'a': null,
      },
      queryParamsHandling: 'merge'
    });

    if (this.account) {
      SessionStorage.setItem('currentAccount', this.account);
      this.dataAccessOrganizations.sendAccount(
        this.dataAccessOrganizations.setOrganization(this.account)
      );
    }
  }

  loadFilters(): void {
    this.store
      .select(fromStore.getDepartmentsLoaded)
      .pipe(take(1))
      .subscribe(loaded => {
        if (!loaded) {
          this.store.dispatch(fromStore.loadDepartments());
        }
      });

    this.store
      .select(fromStore.getRolesLoaded)
      .pipe(take(1))
      .subscribe(loaded => {
        if (!loaded) {
          this.store.dispatch(fromStore.loadRoles());
        }
      });

    this.store
      .select(fromStore.getUnitsLoaded)
      .pipe(take(1))
      .subscribe(loaded => {
        if (!loaded) {
          this.store.dispatch(fromStore.loadUnits());
        }
      });

    this.departments$ = this.store.select(fromStore.getPaginatedDepartments).pipe(retry(1));
    this.roles$ = this.store.select(fromStore.getPaginatedRoles).pipe(retry(1));
    this.units$ = this.store.select(fromStore.getPaginatedUnits).pipe(retry(1));
  }

  searchCollaborator(query: string): void {
    this.searchQuery = query ?? null;
    this.hasFilter = false;
    this.resetParams();
    this.getListingParams();
  }

  loadSearchCollaborator(params: EmployeeParams): void {
    this.collaborators$ = this.employeeService.searchEmployee(this.searchQuery, params).pipe(
      tap(res => (this.total = res?.paginator?.totalRows)),
      map(res => res?.data),
      catchError(err => {
        this.error$.next(err);
        return of();
      })
    );
  }

  loadCollaborators(params: EmployeeParams): void {

    this.collaborators$ = this.employeeService.listEmployees(params).pipe(
      timeout(4000),
      retry(2),
      debounceTime(400),
      tap(res => {
        this.emptyList = res?.status === 204 ? true : false;
        this.total = res?.body?.paginator?.totalRows;
        this.loadFilters();
      }),
      map(employees => employees.body?.data),
      catchError(err => {
        this.error$.next(err);
        this.errorOnLoading = true;
        return of();
      })
    );
  }

  filterParams(params: any): void {
    if (params.units.length || params.departments.length || params.roles.length || params.states.length) {
      this.hasFilter = true;
    } else {
      this.hasFilter = false;
    }
    this.filterState = Object.assign({}, this.convertParams(params));
    this.allItemsSelected = false;
    this.getListingParams();
  }

  convertParams(params): { departments: String[]; roles: String[]; units: String[] } {
    return {
      units: params.units,
      departments: params.departments,
      roles: params.roles
    };
  }

  getPage(page: number): void {
    this.page = page;
    this.router.navigate(['/'], {
      queryParams: { status: this.active, page: page }
    });
    this.getListingParams();
  }

  getPageForLimit(limit: number): void {
    this.limit = limit;
    this.page = 1;
    this.router.navigate(['/'], {
      queryParams: { status: this.active, page: this.page }
    });
    this.getListingParams();
  }

  orderByName(): void {
    this.isAscendingOrder = !this.isAscendingOrder;
    this.getListingParams();
  }

  resetParams(): void {
    this.isAscendingOrder = true;
    this.limit = 10;
    this.router.navigate(['/'], {
      queryParams: { status: 'active', page: 1 }
    });
  }

  setParams(params): EmployeeParams {
    this.active = params?.status || 'active';
    this.page = params?.page || 1;
    return {
      page: this.page,
      pageSize: this.limit,
      order: this.isAscendingOrder ? (this.order = 'asc') : (this.order = 'desc'),
      active: this.active === 'active' ? true : false,
      units: this.filterState.units,
      roles: this.filterState.roles,
      departments: this.filterState.departments
    };
  }

  getListingParams(): void {
    this.route.queryParams.subscribe(params => {
      const data = this.setParams(params);
      this.searchQuery && this.searchQuery.length >= 3 ? this.loadSearchCollaborator(data) : this.loadCollaborators(data);
    });
  }

  /**
   * Check if user is eligible for unico skill trial
   */
  checkEligibilityTrialUnicoSkill(): void {
    this.checkLaunchDarklyFlag('people-trial-unico-skill').then((response: boolean) => {
      this.isUserEligibleForUnicoSkillTrial = response;
      if (this.isUserEligibleForUnicoSkillTrial) {
        this.getUserUnicoSkillTrial();
      }
    });
  }

  /**
   * Get user unico skill trial quantity
   */
  getUserUnicoSkillTrial(): void {
    this.skillService.getUnicoSkillTrial().subscribe((response: ResponseSkillTrial) => {
      this.unicoSkillTrial = response?.data;
    });
  }

  trackEventsSegment(target?: string): void {
    const segmentTrack = {
      button_empty_page: SegmentTrackList.click_button_import_empty_page,
      click_button_import: SegmentTrackList.click_button_import
    }
    this.segment.track(segmentTrack[target])
  }

  /*
   * Methods that toggle items employees
   */
  selectAllItems(event): void {
    if (this.someItemSelected || !event.target.checked) {
      this.clearSelectedAllItems();
      return;
    }

    this.itemsListing.forEach(employee => {
      employee.selected ? null : (this.listItemsSelected = [...this.listItemsSelected, employee.item.id]);
      employee.selected = event.target.checked;
    });
  }

  selectItemsInAllPages(): void {
    this.employeeService.getEmployeeByFilters(this.filterState, true).subscribe(res => {
      const resultsFiltered = res.data.filter(id => !this.listItemsSelected.includes(id));
      this.listItemsSelected = [...resultsFiltered, ...this.listItemsSelected];
    });

    this.allItemsSelected = true;
  }

  clearSelectedAllItems(): void {
    this.typeSelectListing.nativeElement.checked = false;
    this.typeSelectListing.nativeElement.indeterminate = false;
    this.someItemSelected = false;
    this.allItemsSelected = null;
    this.listItemsSelected = [];
  }

  toggleCurrentItem(event: boolean, uid: string): void {
    event && !this.listItemsSelected.includes(uid)
      ? (this.listItemsSelected = [...this.listItemsSelected, uid])
      : (this.listItemsSelected = this.listItemsSelected.filter(i => i !== uid));

    this.someItemSelected = this.listItemsSelected.length && !this.allItemsSelected ? true : false;

    this.listItemsSelected.length > 0
      ? (this.typeSelectListing.nativeElement.indeterminate = true)
      : (this.typeSelectListing.nativeElement.indeterminate = false);
  }

  openDialogChangeStatus(): void {
    const employeeAmount = this.listItemsSelected.length;
    let newStatus = this.active === 'active' ? false : true;
    this.changeStatus = false;

    this.dialogStatusCollaborator = this.dialog.open(UiModalDefaultComponent, {
      disableClose: false
    });
    this.dialogStatusCollaborator.componentInstance.confirmMessage = `Deseja ${newStatus ? 'Ativar' : 'Inativar'} ${employeeAmount} ${
      employeeAmount > 1 ? 'colaboradores ?' : 'colaborador ?'
    }`;
    this.dialogStatusCollaborator.componentInstance.menssageSubtitle = `Os colaboradores serão movidos para a aba ${
      newStatus ? '”Ativos” e seus serviços serão reativados.' : '”Inativos” e seus serviços serão desligados.'
    }`;
    this.dialogStatusCollaborator.componentInstance.confirmMessageButton = `${this.active === 'active' ? 'Inativar' : 'Ativar'}`;
    this.dialogStatusCollaborator.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.employeeService.changeManyEmployeeStatus(this.listItemsSelected, newStatus).subscribe(() => {
          this.getListingParams();
          this.clearSelectedAllItems();
          return;
        });
      }
      this.changeStatus = true;
    });
  }

  trackByItems(index: number, collaborator: EmployeeData): number { return index; }

  /**
   * Check feature flag on launchdarkly
   */
  async checkLaunchDarklyFlag(flag: string): Promise<boolean> {
    const ctx = {
        kind: 'user',
        key: SessionStorage.getItem('organization')
    };
    await this.launchDarklyService?.overrideContext({ context: ctx });
    return this.launchDarklyService?.evaluateBoolean({ flagKey: flag });
  }

  /**
   * Active unico skill trial for selected users
   */
  activeTrialSelectedUsers(): void {
    this.skillService.activeSkillTrial(this.listItemsSelected).pipe(
      shareReplay(1),
      catchError(err => {
        // todo: error feedback
        this.error$.next(err);
        return of(err);
      })
    ).subscribe((response: any) => {
      // todo: success feedback
    });
  }
}
